import './form-select-field.scss';

import { Controller, useFormContext } from 'react-hook-form';
import {
  CountryDetails,
  EntityDetails,
  IBrokerEntityInfo,
  IndustrySectorDetails,
  ProductDetails,
  ProgramDetails,
  Region,
  useGetCountryQuery,
  useGetEntities,
  useGetIndustrySectorsQuery,
  useGetProductsQuery,
  useGetProgramsQuery,
} from '@converge/graphql';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material';

import {
  ApplicationSelectOptions,
  NAICS_PLACEHOLDER_CODE,
} from '@converge/data';
import { FormFieldContainer } from './form-field-container';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import { NewConvergeInputLabel } from './new-converge-input-label';
import { NewFromSelectField } from './new-form-select-field';
import { useGetRegionsQuery } from '@converge/graphql';

type Value = string | number | null;

interface ListOption<D, V extends Value = string> {
  value: V;
  label: string;
  data: D;
}

interface FormSelectFieldProps<D = unknown, V extends Value = string> {
  setIsPartnered?: SetStateType<boolean>;
  label?: string | null;
  className?: string;
  placeholder?: string;
  required?: boolean;
  value?: string | null;
  name: string;
  containerWidth?: number;
  sx?: SxProps<Theme> | null;
  options?: D[];
  onChange?: (event: SelectChangeEvent<NonNullable<V>>) => void;
  mapToOption?: (
    item: D
  ) => Omit<ListOption<D, V | null>, 'data'> &
    Partial<Pick<ListOption<D, V | null>, 'data'>>;
  isLoading?: boolean;
  onChangeOption?: (item: ListOption<D, V>) => void;
  hideLabel?: boolean;
  debug?: boolean;
  disabled?: boolean;
  setIsPartnerDisabled?: Dispatch<SetStateAction<boolean>>;
}

interface PreConfiguredFormSelectFieldProps<
  D = unknown,
  V extends Value = string
> extends Omit<
    FormSelectFieldProps<D, V>,
    'options' | 'mapToOption' | 'isLoading'
  > {
  setIsPartnerDisabled?: Dispatch<SetStateAction<boolean>>;
  setCapacityProviderDisabled?: Dispatch<SetStateAction<boolean>>;
  CapacityProviderDisabled?: boolean;
  dataTestId?: string;
}
type SetStateType<T> = boolean | ((value: T) => void);
interface PreConfiguredStaticFormSelectFieldProps<
  D = unknown,
  V extends Value = string
> extends PreConfiguredFormSelectFieldProps<D, V> {
  optionsType: OptionsDataType;
}

export type OptionsDataType = keyof typeof ApplicationSelectOptions.optionsData;

export const FormSelectField = <D, V extends Value = string>({
  name,
  label = name,
  required,
  placeholder,
  className,
  containerWidth = 6,
  options,
  mapToOption,
  isLoading,
  onChangeOption,
  hideLabel = false,
  value: valueProp,
  setIsPartnered,
  debug,
  sx,
  setIsPartnerDisabled,
  ...props
}: FormSelectFieldProps<D, V>) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const listOptions = useMemo((): ListOption<D, V>[] => {
    if (isLoading) {
      return [];
    }

    if (!options?.length) {
      return [];
    }

    if (mapToOption) {
      return options.map((item) => {
        const option = mapToOption(item);
        option.data = item;
        return option as ListOption<D, V>;
      });
    }

    return options as ListOption<D, V>[];
  }, [isLoading, mapToOption, options]);

  const error = errors[name];

  const currentValue = valueProp ?? watch(name);

  const selectedListOption = listOptions.find(
    (option) => option.value === currentValue || option.label === currentValue
  );

  useEffect(() => {
    if (!selectedListOption) {
      return;
    }
    if (selectedListOption.value !== currentValue) {
      setValue(name, selectedListOption.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  }, [setValue, name, selectedListOption, currentValue]);

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required,
      }}
      render={({ field: { ref, onChange, ...otherField } }) => {
        return (
          <FormFieldContainer md={containerWidth}>
            {hideLabel === false && (
              <NewConvergeInputLabel label={label} required={required} />
            )}
            <Select
              sx={sx}
              fullWidth
              disabled={isLoading}
              size="small"
              displayEmpty
              placeholder="Please select"
              className="form-select-field"
              onChange={(event) => {
                onChange(event);
                if (onChangeOption) {
                  const foundOption = listOptions.find(
                    (listOption) => listOption.value === event.target.value
                  );
                  if (!foundOption) {
                    throw new Error(
                      `No option found for "${event.target.value}"`
                    );
                  }
                  onChangeOption(foundOption);
                }
              }}
              {...otherField}
              {...props}
              value={selectedListOption?.value ?? ''}
              error={!!error}
            >
              <MenuItem disabled value="">
                <em className="placeholder">
                  {placeholder || 'Please Select'}
                </em>
              </MenuItem>
              {listOptions.map((item, index) => {
                return (
                  <MenuItem
                    key={`${item.value ?? index}`}
                    value={item.value as string}
                  >
                    {item.label}
                  </MenuItem>
                );
              })}
            </Select>
            {error && (
              <FormHelperText
                error
                className="form-select-form-helper-text-error"
              >
                {`${error?.message ?? 'Error'}`}
              </FormHelperText>
            )}
            {debug && (
              <FormHelperText>
                <pre className="form-select-form-helper-text-debug">
                  {JSON.stringify(
                    {
                      value: otherField.value,
                      name: otherField.name,
                      options,
                    },
                    null,
                    2
                  )}
                </pre>
              </FormHelperText>
            )}
          </FormFieldContainer>
        );
      }}
    />
  );
};

export const CountryFormSelectField = (
  props: PreConfiguredFormSelectFieldProps<CountryDetails>
) => {
  const { loading, countries } = useGetCountryQuery();
  return (
    <FormSelectField<CountryDetails>
      {...props}
      options={countries}
      isLoading={loading}
      mapToOption={(country) => {
        return {
          value: country.uuid ?? '',
          label: country.name ?? 'N/A',
        };
      }}
    />
  );
};

export const NewCountryFormSelectField = (
  props: PreConfiguredFormSelectFieldProps<CountryDetails>
) => {
  const { loading, countries } = useGetCountryQuery();

  // const defaultCountry = countries.find(
  //   (country) => country.iso2Code === 'US'
  // )?.uuid;

  return (
    <NewFromSelectField<CountryDetails>
      {...props}
      options={countries}
      // value={''}
      isLoading={loading}
      mapToOption={(country) => {
        return {
          value: country.uuid ?? '',
          label: country.name ?? 'N/A',
        };
      }}
    />
  );
};

export const NewRegionFormSelectField = (
  props: PreConfiguredFormSelectFieldProps<Region> & {
    selectedCountry?: string;
    setRegionName?: Dispatch<SetStateAction<string>>;
    selectedState?: string;
    value?: string;
    dataTestId?: string;
  }
) => {
  const countryCode = { countryCode: props.selectedCountry || 'US' };

  const { loading, regions } = useGetRegionsQuery(countryCode);

  return (
    <NewFromSelectField<Region>
      {...props}
      options={regions}
      placeholder="Select State"
      isLoading={loading}
      mapToOption={(region) => {
        props.setRegionName && props.setRegionName(region.type || '');
        return {
          value: region.uuid ?? '',
          label: region.name ?? 'N/A',
        };
      }}
    />
  );
};

export const RegionFormSelectField = (
  props: PreConfiguredFormSelectFieldProps<Region> & {
    selectedCountry?: string;
    setRegionName?: Dispatch<SetStateAction<string>>;
  }
) => {
  const countryCode = { countryCode: props.selectedCountry || 'US' };

  const { loading, regions } = useGetRegionsQuery(countryCode);
  return (
    <FormSelectField<Region>
      {...props}
      label={'STATE'}
      options={regions}
      isLoading={loading}
      placeholder="Select State"
      mapToOption={(region) => {
        props.setRegionName && props.setRegionName(region.type || '');
        return {
          value: region.uuid ?? '',
          label: region.name ?? 'N/A',
        };
      }}
    />
  );
};

export const IndustrySectorFormSelectField = (
  props: PreConfiguredFormSelectFieldProps<IndustrySectorDetails>
) => {
  const { loading, industrySectors } = useGetIndustrySectorsQuery();
  const selectableSectors = (industrySectors ?? []).filter(
    (sector) => sector.naicsCode !== NAICS_PLACEHOLDER_CODE
  );
  return (
    <FormSelectField<IndustrySectorDetails>
      {...props}
      options={selectableSectors}
      isLoading={loading}
      mapToOption={(industrySector) => {
        return {
          value: (industrySector.naicsCode ?? -1) as unknown as string,
          label: `${industrySector.name} (${industrySector.naicsCode})`,
        };
      }}
    />
  );
};
export const ProgramFormSelectField = (
  props: PreConfiguredFormSelectFieldProps<ProgramDetails>
) => {
  const { loading, programs = [] } = useGetProgramsQuery({});

  return (
    <FormSelectField<ProgramDetails>
      {...props}
      // onChangeOption={(item) => handleProgramChange(item)}
      disabled={true}
      options={programs}
      isLoading={loading}
      containerWidth={12}
      mapToOption={(program) => {
        return {
          value: program?.uuid ?? '',
          label: program?.name ?? 'N/A',
        };
      }}
    />
  );
};

export const PartnerFormSelectField = (
  props: PreConfiguredFormSelectFieldProps
) => {
  const { entities: partners = [] } = useGetEntities({
    isProgramPartner: true,
  });

  const PARTNER_OPTIONS = [
    { value: 'no partner', label: 'No Partner' },
    ...partners.map((partner) => ({
      value: partner?.uuid ?? '',
      label: partner?.name ?? 'N/A',
    })),
  ];

  return (
    <FormSelectField
      disabled={true}
      {...props}
      // onChangeOption={(item) => handleProgramChange(item)}
      options={PARTNER_OPTIONS || []}
    />
  );
};

export const EntitySelectField = ({
  isInsurer,
  isCapacityProvider,
  isProgramPartner,
  isAgency,
  isPartner,
  CapacityProviderDisabled,
  ...props
}: PreConfiguredFormSelectFieldProps<EntityDetails> & {
  isInsurer?: boolean;
  isCapacityProvider?: boolean;
  isAgency?: boolean;
  isProgramPartner?: boolean;
  isPartner?: boolean;
  CapacityProviderDisabled?: boolean;
}) => {
  const { loading, entities = [] } = useGetEntities({
    isInsurer,
    isAgency,
    isCapacityProvider,
    isProgramPartner: true,
  });

  const handleProviderChange = (
    item: ListOption<
      {
        uuid: string;
        referenceId: string;
        legalName: string;
        name: string | null | undefined;
        address1: string;
        address2: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
        brokerInfo: IBrokerEntityInfo;
      },
      string
    >
  ) => {
    if (props.setCapacityProviderDisabled) {
      if (item) {
        props.setCapacityProviderDisabled(false);
      } else {
        props.setCapacityProviderDisabled(true);
      }
    }
  };

  return (
    <FormSelectField<EntityDetails>
      onChangeOption={(item) => handleProviderChange(item)}
      {...props}
      options={entities}
      disabled={props.disabled ? props.disabled : isProgramPartner}
      isLoading={loading}
      mapToOption={(entity) => {
        return {
          value: entity.uuid,
          label: entity.name ?? 'N/A',
        };
      }}
    />
  );
};

export const ProductFormSelectField = (
  props: PreConfiguredFormSelectFieldProps<ProductDetails>
) => {
  const { loading, products = [] } = useGetProductsQuery();
  return (
    <FormSelectField<ProductDetails>
      {...props}
      options={products}
      isLoading={loading}
      mapToOption={(product) => {
        return {
          value: product.uuid ?? '',
          label: product.name ?? 'N/A',
        };
      }}
    />
  );
};

export const StaticFormSelectField = <D, V extends Value = string>({
  optionsType,
  ...props
}: PreConfiguredStaticFormSelectFieldProps<ListOption<D, V>>) => {
  const data = ApplicationSelectOptions.optionsData[optionsType]?.map(
    (item): ListOption<D, V> => {
      return {
        label: item.name,
        value: item.uuid as V,
        data: item as D,
      };
    }
  );

  return <FormSelectField<ListOption<D, V>> {...props} options={data} />;
};
